import { dutchStems } from '../utils/productUtils'

/** @import { Offer, CategoryFilter, OfferWeek, SortOption, SupermarketId } from '../../types.js' */

// --- Internal helpers ---

function matchesSearch(name, q, qStems, semanticMatches) {
  if (name.includes(q)) return true

  const words = name.split(/\s+/)
  const stemMatch = qStems.some(qs =>
    words.some(w => dutchStems(w).some(ws => ws.startsWith(qs) || qs.startsWith(ws)))
  )
  if (stemMatch) return true

  if (!semanticMatches) return false
  if (semanticMatches.has(name)) return true
  for (const match of semanticMatches) {
    if (name.includes(match) || match.includes(name)) return true
  }
  return false
}

function filterByWeek(offers, selectedWeek) {
  if (selectedWeek === 'upcoming') return offers.filter(o => o.week === 'upcoming')
  return offers.filter(o => o.week !== 'upcoming')
}

function filterOffers(offers, { selectedSupermarkets, selectedCategory, selectedWeek, searchQuery, semanticMatches }) {
  let result = filterByWeek(offers, selectedWeek)

  if (selectedSupermarkets.size > 0) {
    result = result.filter(o => selectedSupermarkets.has(o.supermarket))
  }
  if (selectedCategory !== 'all') {
    result = result.filter(o => o.category === selectedCategory)
  }
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim()
    const qStems = dutchStems(q)
    result = result.filter(o => matchesSearch(o.productName.toLowerCase(), q, qStems, semanticMatches))
  }
  return result
}

/** @type {Record<SortOption, ((a: Offer, b: Offer) => number)|undefined>} */
const SORT_FNS = {
  priceAsc: (a, b) => a.offerPrice - b.offerPrice,
  priceDesc: (a, b) => b.offerPrice - a.offerPrice,
  discountDesc: (a, b) => {
    const discA = a.originalPrice ? ((a.originalPrice - a.offerPrice) / a.originalPrice) : 0
    const discB = b.originalPrice ? ((b.originalPrice - b.offerPrice) / b.originalPrice) : 0
    return discB - discA
  },
  supermarket: (a, b) => a.supermarket.localeCompare(b.supermarket),
}

// --- Exported selectors ---

/** @returns {Array<Offer>} */
export function selectOffers(state) {
  if (!state.rawData?.offers) return []
  const result = filterOffers(state.rawData.offers, state)
  const sortFn = SORT_FNS[state.sortBy]
  if (sortFn) result.sort(sortFn)
  return result
}

/** @returns {Array<SupermarketId>} */
export function selectAvailableSupermarkets(state) {
  if (!state.rawData?.offers) return []
  const weekOffers = filterByWeek(state.rawData.offers, state.selectedWeek)
  return [...new Set(weekOffers.map(o => o.supermarket))]
}

/** @returns {Record<CategoryFilter, number>} */
export function selectCategoryCounts(state) {
  if (!state.rawData?.offers) return { all: 0 }
  let base = filterByWeek(state.rawData.offers, state.selectedWeek)
  if (state.selectedSupermarkets.size > 0) {
    base = base.filter(o => state.selectedSupermarkets.has(o.supermarket))
  }
  /** @type {Record<string, number>} */
  const counts = { all: base.length }
  for (const o of base) {
    if (o.category) {
      counts[o.category] = (counts[o.category] || 0) + 1
    }
  }
  return counts
}

export function selectHasActiveFilters(state) {
  return (
    state.selectedSupermarkets.size > 0 ||
    state.selectedCategory !== 'all' ||
    state.searchQuery.trim() !== ''
  )
}

export function selectHasUpcoming(state) {
  if (!state.rawData?.offers) return false
  return state.rawData.offers.some(o => o.week === 'upcoming')
}

export function selectScrapedAt(state) {
  if (state.selectedWeek === 'upcoming') {
    return state.rawData?.previewScrapedAt ?? state.rawData?.scrapedAt ?? null
  }
  return state.rawData?.scrapedAt ?? null
}

export function selectTotalCount(state) {
  if (!state.rawData?.offers) return 0
  return filterByWeek(state.rawData.offers, state.selectedWeek).length
}

export function selectFilteredCount(state) {
  return selectOffers(state).length
}
